import React, { useState, useCallback, useEffect } from 'react';
import {
  Plus, Search, Users, Pencil, Trash2, Mail, MapPin, Phone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { customersAPI } from '../lib/api';
import { toast } from 'sonner';

const hFont = { fontFamily: 'Manrope, sans-serif' };

const emptyForm = { name: '', address_line1: '', postal_code: '', city: '', country_code: 'CH', email: '', phone: '', vat_id: '', registered_address: '', notes: '' };

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customersAPI.getAll(search ? { search } : undefined);
      setCustomers(res.data.items || []);
    } catch { setCustomers([]); }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '',
      address_line1: c.address_line1 || '',
      postal_code: c.postal_code || '',
      city: c.city || '',
      country_code: c.country_code || 'CH',
      email: c.email || '',
      phone: c.phone || '',
      vat_id: c.vat_id || '',
      registered_address: c.registered_address || '',
      notes: c.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (editing) {
        await customersAPI.update(editing.id, form);
        toast.success('Customer updated');
      } else {
        await customersAPI.create(form);
        toast.success('Customer created');
      }
      setShowForm(false);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save customer');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await customersAPI.delete(confirmDelete.id);
      toast.success('Customer deleted');
      setConfirmDelete(null);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q);
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-stone-900" style={hFont}>Customers</h2>
          <p className="text-sm text-stone-500">Manage your customer master data</p>
        </div>
        <Button onClick={openCreate} className="bg-stone-900 hover:bg-stone-800 text-white h-9 px-5 text-sm font-semibold rounded-full" data-testid="new-customer-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Customer
        </Button>
      </div>

      <form onSubmit={handleSearch} className="flex items-center gap-3 max-w-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers..." className="pl-9 h-9 text-sm" data-testid="customer-search" />
        </div>
        <Button type="submit" variant="outline" size="sm" className="h-9">Search</Button>
      </form>

      {loading ? (
        <div className="text-center py-12 text-sm text-stone-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No customers yet. Create your first customer to get started.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(c => (
            <div key={c.id} className="flex items-center justify-between border border-stone-200 rounded-xl bg-white px-5 py-4 hover:border-stone-300 transition-colors" data-testid={`customer-card-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm text-stone-900 truncate" style={hFont}>{c.name}</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-xs text-stone-500">
                  {c.email && (
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                  )}
                  {c.city && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.postal_code} {c.city}</span>
                  )}
                  {c.phone && (
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-4">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setConfirmDelete(c)} data-testid={`delete-customer-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={hFont}>{editing ? 'Edit Customer' : 'New Customer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold text-stone-600 mb-1">Company / Name *</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="customer-form-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-600 mb-1">Address</Label>
              <Input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} data-testid="customer-form-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1">Postal Code</Label>
                <Input value={form.postal_code} onChange={e => set('postal_code', e.target.value)} data-testid="customer-form-postal" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1">City</Label>
                <Input value={form.city} onChange={e => set('city', e.target.value)} data-testid="customer-form-city" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1">Country</Label>
                <Input value={form.country_code} onChange={e => set('country_code', e.target.value)} data-testid="customer-form-country" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1">Email</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="customer-form-email" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1">Phone</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)} data-testid="customer-form-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-600 mb-1">VAT ID</Label>
              <Input value={form.vat_id} onChange={e => set('vat_id', e.target.value)} placeholder="e.g. CHE-123.456.789" data-testid="customer-form-vat-id" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-600 mb-1">Registered Address</Label>
              <Input value={form.registered_address} onChange={e => set('registered_address', e.target.value)} placeholder="Legal/registered company address" data-testid="customer-form-registered-address" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-600 mb-1">Notes</Label>
              <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." data-testid="customer-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="customer-form-submit">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={hFont}>Delete Customer</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600 py-2">
            Are you sure you want to delete <strong>{confirmDelete?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-customer">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
